import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';
import linkstate from 'linkstate';

import fields from '../../lib/fields';

import FormFields from '../../components/form/fields';
import ImageScroller from '../../components/imagescroller';
import HelpText from '../../components/gui/helpText';
import Input from '../../components/form/input';
import GuiList from '../../components/gui/list';
import UserProfileInline from '../../components/user/profileInline';
import Camera from '../../components/gui/camera';

import localUtil from '../../lib/util';
import { requiredStarts } from '../../lib/conventionPayState';
import IpscSignupFields from './ipscSignupFields';
import { isIpscConvention, ipscParticipantFromSignup } from '../../lib/ipscParticipant';
import { QRCodeSVG } from 'qrcode.react';

@withText(props => ({
    otpTitle: <Text id='input.otp-input'>Enter OTP</Text>,
}))
@observer
class ConventionViewSignup extends Component {
  	constructor(props) {
        super(props);
        this.state = {};
    }

    updateIpscField = (name, value) => {
        this.setState({ [name === 'class' ? 'ipscDivision' : name]: value });
    }

    otpInput = e => {
        const userOtpIsValid = e.target.value.length === 6;
        this.setState({
            userOtp: e.target.value,
            userOtpIsValid,
        });
    }

    checkOtp = async e => {
        const { userOtp } = this.state;
        const { conventionStore } = this.props.stores;
        const { convention, jwtToken } = conventionStore;
        const response = await conventionStore.checkOtp(convention.uuidv4, userOtp);
        if (response.status === 200) {
            route(`/convention/${jwtToken}/signup`);
        } else {
            this.setState({
                userOtp: '',
                userOtpError: true,
            });
        }
    }

    closeDrawers = () => {
        const { appState } = this.props.stores;
        appState.toggleDrawer();
    }

    signUp = async () => {
        const { loadAll = () => {} } = this.props;
        const { appState, conventionStore, userStore } = this.props.stores;
        const { convention, jwtToken } = conventionStore;
        const { user } = userStore;

        // Not logged in: remember the intent, send to login, and auto-signup on return.
        if (!user || !user.id) {
            util.set('conventionSignupAfterLogin', convention.id);
            util.set('conventionSignupToken', jwtToken);
            appState.setWantedPage(`/convention/${convention.id}`);
            this.closeDrawers();
            route('/login');
            return;
        }

        let shootingClass;
        let extra = {};
        if (isIpscConvention(convention)) {
            const n = ipscParticipantFromSignup({
                class: this.state.ipscDivision, powerFactor: this.state.powerFactor, ipscCategory: this.state.ipscCategory,
            });
            if (!n.class) {
                appState.addToast({ type: 'warning', message: 'Velg divisjon først' }, 5000);
                return;
            }
            shootingClass = n.class;
            extra = { powerFactor: n.powerFactor, ipscCategory: n.ipscCategory };
        }
        const response = await conventionStore.signUp(user, shootingClass, extra);
        if (response && response.errorCode === 'ALREADY_SIGNED_UP') {
            appState.addToast({
                type: 'warning',
                message: response.message || 'Du er allerede påmeldt dette stevnet',
            }, 5000);
        } else if (convention?.id) {
            appState.trackConvention('signup', convention);
        }
        await loadAll();
    }

    openStageSelector = () => {
        const { drawerLevel = 1, loadAll = () => {} } = this.props;
        const { appState, conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
        const { drawerHeightLarge } = appState;
        appState.openDrawer('conventionStageSelector', {
            height: drawerHeightLarge,
            callback: async () => {
                await loadAll();
            },
        }, drawerLevel + 1);
    }

    formatCountdown = (totalSeconds) => {
        if (totalSeconds < 0) return '';

        const months = Math.floor(totalSeconds / (30 * 24 * 60 * 60));
        const days = Math.floor((totalSeconds % (30 * 24 * 60 * 60)) / (24 * 60 * 60));
        const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
        const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
        const seconds = Math.floor(totalSeconds % 60);

        const parts = [];
        if (months > 0) parts.push(`${months}m`);
        if (days > 0 || months > 0) parts.push(`${days}d`);
        parts.push(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);

        return parts.join(' ');
    }

    render() {
        const {
            userOtp,
            userOtpIsValid,
            userOtpError,
        } = this.state;
        const {
            otpTitle,
            isInTheFuture,
            isInThePast,
            isRegistrationOpen,
            isOngoing,
            signedUp,
            isConventionAdmin,
            countdown = {},
        } = this.props;
        const { appState, userStore, conventionStore, conventionResultStore } = this.props.stores;
        const { mainView, subView, isDevelopment, path } = appState;
        const { user = {}, isAdmin, isCustomerAdmin, isTester, isVeterinary, language, searchUsersResult } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        // `convention` defaults to `{}` because a not-found / inaccessible convention
        // loads as `undefined` (see the updateItem guard in stores/convention.js).
        const { convention = {}, jwtToken, otp = '' } = conventionStore;

        const otpFirstPart = otp.slice(0, 3);
        const otpSecondPart = otp.slice(3, 6);

        const isOnAnyStage = convention.stages?.some((stage, idx) => {
            const stageParticipants = convention[`participants${idx + 1}`] || [];
            return stageParticipants.some(p => p.id === user.id);
        });

        // During the stevne a signed-up shooter needs nothing from this box.
        // Admins are only exempt for the self-registration QR/OTP block, which
        // exists solely when Selvregistrering is on and the open-signup toggle
        // (registrationIsOpen) is off — otherwise the box would render empty.
        const adminNeedsQr = isConventionAdmin && convention.selfSignup && !convention.registrationIsOpen;
        if (signedUp && isOngoing && !adminNeedsQr) {
            return <></>;
        }

        return (<>
            <div class={`d-flex flex-column justify-content-center align-items-center mt-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                {/* No signup CTA (or login-to-signup prompt) once the convention is over,
                    even if registrationIsOpen was never switched off on the record. */}
                {!isInThePast && (convention.registrationIsOpen ? <>
                    {isInTheFuture && !signedUp ? <>
                        <h3 class='w-100 mb-2'><Text id='convention.registration-is-open'>Registration is open!</Text></h3>
                    </> : <></>}
                    {signedUp ? <></> : <>
                        {isIpscConvention(convention) && !!user.id && (
                            <div class='w-100 text-start'>
                                <IpscSignupFields
                                    division={this.state.ipscDivision || ''}
                                    powerFactor={this.state.powerFactor || 'minor'}
                                    ipscCategory={this.state.ipscCategory || ''}
                                    onChange={this.updateIpscField}
                                />
                            </div>
                        )}
                        <button type='button' class='btn btn-lg btn-success rounded-pill w-100 mt-3 mb-3' onClick={this.signUp}>
                            <i class='fa-duotone fa-user-plus me-2' />
                            <Text id='convention.sign-up'>Sign up for convention</Text>
                        </button>

                        {isConventionAdmin ? <>
                            <div class='w-100 text-danger my-3'>
                                <i class='fa-regular fa-exclamation-triangle me-2' />
                                <Text id='convention.is-admin-is-open-reg'>You are admin of this convention. Signup to this convention is open and does not require a code. If you want to participate you have to signup as well. All other admin functions are the same.</Text>
                            </div>
                        </> : <></>}
                    </>}
                </> : <>
                    {!convention.selfSignup ? <>
                        {/* Manual show-up convention (Selvregistrering off): the QR/OTP
                            self-registration flow makes no sense here — signup happens at
                            the venue with the organizers, so show a plain frontpage box. */}
                        {!signedUp && (
                            <div class='alert alert-info rounded-lg d-flex flex-column align-items-center w-100 mb-0' role='alert'>
                                <i class='fa-duotone fa-person-walking-arrow-right mb-2' style='font-size: 3.0em;' />
                                <h5 class='mb-1'><Text id='convention.manual-signup-title'>Signup at the event</Text></h5>
                                <div class='text-center'>
                                    <Text id='convention.manual-signup-info'>Signup for this convention happens at the venue. Show up and the organizers will register you.</Text>
                                </div>
                                {convention.signupProduct > 0 && convention.signupProductPrice > 0 && (
                                    <span class='badge text-bg-light mt-2'>
                                        <i class='fa-solid fa-coins me-1' />
                                        <Text id='convention.manual-signup-fee' fields={{ price: convention.signupProductPrice }}>Signup fee: {convention.signupProductPrice} kr</Text>
                                    </span>
                                )}
                            </div>
                        )}
                    </> : <>
                    {!user || !user.id ? <>
                        <div class='alert alert-danger rounded-lg d-flex flex-column align-items-center w-100' role='alert'>
                            <i class='fa-duotone fa-right-to-bracket mb-2' style='font-size: 3.0em;' />
                            <Text id='convention.must-login'>You must login to signup for this convention.</Text>
                        </div>
                        <a class='btn btn-primary btn-lg btn-block rounded-pill mt-2 mb-5' href='/login' onClick={this.closeDrawers}>
                            <i class='fa-duotone fa-right-to-bracket me-2' /><Text id='convention.log-in'>Log in</Text>
                        </a>
                    </> : <>
                        {isConventionAdmin ? <>
                            {isRegistrationOpen ? <>
                                <QRCodeSVG value={`https://${window.location.hostname}/#/convention/${jwtToken}/signup`} size={256} />
                                <a href={`/convention/${jwtToken}/signup`} onClick={this.closeDrawers}><Text id='convention.sign-up-for-admin'>Sign up for admins!</Text></a><br />
                                <div
                                    class='d-flex text-center'
                                    style={`
                                        font-size: 2.5rem;
                                        font-weight: bold;
                                        letter-spacing: 10px;
                                        font-family: 'Courier New', Courier, monospace;
                                    `}
                                >
                                    {otpFirstPart} {otpSecondPart}
                                </div>
                            </> : <>
                                {isInTheFuture ? <>
                                    <div class='alert alert-success rounded-lg' role='alert'>
                                        <Text id='convention.is-admin'>You are admin of this convention. A QR-Code will appear here 1 hour before the start.</Text>
                                    </div>
                                </> : <></>}
                            </>}
                        </> : <>
                            {isRegistrationOpen ? <>
                                {!signedUp ? <>
                                    <div class='alert alert-danger rounded-lg d-flex flex-column align-items-center w-100' role='alert'>
                                        <i class='fa-duotone fa-user-magnifying-glass mb-2' style='font-size: 1.5em;' />
                                        <Text id='convention.not-admin'>Find an admin and ask for the QR-code to attend this convention.</Text>
                                    </div>

                                    {/* <Camera fps={5} stores={this.props.stores} /> */}

                                    <div class='d-flex flex-column justify-content-center  w-100 mt-4'>
                                        <Text id='convention.log-in-with-code'>Log in with code:</Text>

                                        <div class='input-group'>
                                            <div class='input-group-text'>
                                                <span class='input-group-text rounded-pill-left'>
                                                    <i class='fa-duotone fa-key'></i>
                                                </span>
                                            </div>
                                            <input
                                                class={`form-control form-control-lg rounded-pill-right`}
                                                type='text'
                                                value={userOtp}
                                                placeholder={`${otpTitle}`}
                                                onInput={this.otpInput}
                                                size='6'
                                                maxLength={6}
                                                // onBlur={this.clearSearch}
                                            />
                                        </div>
                                        {userOtpIsValid && <div class='d-flex flex-row justify-content-center mt-2'>
                                            <button
                                                // disabled={disabled}
                                                class={`btn btn-primary rounded-pill`}
                                                type='button'
                                                onClick={this.checkOtp}
                                            >
                                                <i class='fa-duotone fa-right-to-bracket' /> <span class=''><Text id='convention.login-with-code'>Login with code</Text></span>
                                            </button>
                                        </div>}

                                        {userOtpError ? <>
                                            <div class='alert alert-danger rounded-lg mt-2' role='alert'>
                                                <Text id='convention.wrong-code'>Wrong code!</Text>
                                            </div>
                                        </> : <></>}

                                    </div>

                                </> : <>
                                    <i class='fa-regular fa-face-smile text-success' style='font-size: 3.0em;' />
                                </>}
                            </> : <></>}
                        </>}
                    </>}
                    </>}
                </>)}

                {isInTheFuture ? <>
                    {/* <Text id='convention.not-valid-date'>This convention is not active.</Text> */}
                    <div class='d-flex flex-column justify-content-center mt-2'>
                        <Text id='convention.starts-in'>Starts in:</Text>
                        <span class='display-4 text-monospace'>
                            {countdown && countdown.seconds >= 0 ? <>
                                {this.formatCountdown(countdown.seconds)}
                            </> : <></>}
                        </span>
                    </div>
                </> : <>
                    {!isRegistrationOpen ? <>
                        {convention.startDate ? <>
                            <div class='d-flex flex-column justify-content-center mt-0'>
                                <Text id='convention.has-been'>Has been:</Text>
                                <span class='text-monospace' style='font-size: 1.5em;'>
                                    {util.formatDate(convention.startDate, { hour12: false, hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        </> : <></>}
                    </> : <></>}
                </>}

                {signedUp && !isOngoing ? <>
                    <div class='d-flex flex-row justify-content-center'>
                        <div class='text-success'>
                            <i class='fa-solid fa-check text-success' />
                            <small class='ms-2'>
                                {isInTheFuture || isRegistrationOpen ? <>
                                    <Text id='convention.signed-up'>You are signed up to this convention!</Text>
                                </> : <>
                                    <Text id='convention.signed-up-done'>You have attended this convention!</Text>
                                </>}
                            </small>
                        </div>
                    </div>

                    {convention.signupProduct > 0 && (isInTheFuture || isRegistrationOpen) ? <>
                        {(() => {
                            const currentParticipant = convention.participants?.find(p => p.id === user.id);
                            const paidStartsCount = currentParticipant?.paidStartsCount || 0;

                            if (paidStartsCount === 0) {
                                return null;
                            }

                            let lagCount = 0;
                            if (convention.stages && convention.stages.length) {
                                convention.stages.forEach((stage, idx) => {
                                    const stageParticipants = convention[`participants${idx + 1}`] || [];
                                    if (stageParticipants.find(p => p.id === user.id)) {
                                        lagCount += 1;
                                    }
                                });
                            }

                            // Competitions: one fee per Lag. Trainings: one fee in total.
                            const feeStarts = requiredStarts(convention, lagCount);
                            const totalLag = Math.max(feeStarts, paidStartsCount);
                            const allPaid = paidStartsCount >= feeStarts;

                            return (
                                <div class='d-flex flex-column align-items-center mt-2 w-100'>
                                    <span class={`badge ${allPaid ? 'text-bg-success' : 'text-bg-info'} mb-2`}>
                                        <i class={`fa-solid ${allPaid ? 'fa-circle-check' : 'fa-coins'} me-1`} />
                                        {lagCount > 0 ? (
                                            <Text id='convention.starts-paid-of' fields={{ paid: paidStartsCount, total: totalLag }}>
                                                {paidStartsCount} of {totalLag} starts paid
                                            </Text>
                                        ) : (
                                            <Text id='convention.starts-paid' fields={{ paid: paidStartsCount }}>
                                                {paidStartsCount} starts paid
                                            </Text>
                                        )}
                                    </span>
                                </div>
                            );
                        })()}
                    </> : <></>}

                    {isInTheFuture || isRegistrationOpen ? <>
                        <UserProfileInline stores={this.props.stores} shootingSignup={true} showDone={true} requireNsfId={true} hideEmailSettings={true} hideColor={true} />
                    </> : <></>}
                    {/* Button to open stage selector for signed up users — gated on paid signup or admin */}
                    {(isInTheFuture || isRegistrationOpen)
                        && convention.selfSignup
                        && convention.stages && convention.stages.length > 0
                        && (isConventionAdmin
                            || (convention.signupProduct > 0
                                ? (convention.participants?.find(p => p.id === user.id)?.paidStartsCount || 0) > 0
                                : true)) ? <>
                        <button
                            class='btn btn-lg btn-success rounded-pill mt-3 w-100'
                            onClick={this.openStageSelector}
                        >
                            <i class='fa-duotone fa-calendar-check me-2' />
                            <Text id='convention.choose-stage'>Choose stage</Text>
                        </button>
                    </> : <></>}
                    {(isInTheFuture || isRegistrationOpen)
                        && !convention.selfSignup
                        && !isConventionAdmin
                        && convention.stages && convention.stages.length > 0
                        && !isOnAnyStage ? <>
                        <div class='alert alert-info rounded-lg d-flex align-items-center w-100 mt-3 mb-0' role='alert'>
                            <i class='fa-duotone fa-circle-info me-2' />
                            <Text id='convention.admin-will-add-to-stage'>An administrator will add you to a team.</Text>
                        </div>
                    </> : <></>}
                </> : <></>}

            </div>


            {/* {signedUp ? <>
                <div class={`d-flex flex-column mt-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <div class='d-flex flex-row justify-content-center'>
                        <div class='text-success'>
                            <i class='fa-solid fa-check text-success' />
                            <span class='ms-2'>
                                {isInTheFuture || isRegistrationOpen ? <>
                                    <Text id='convention.signed-up'>You are signed up to this convention!</Text>
                                </> : <>
                                    <Text id='convention.signed-up-done'>You have attended this convention!</Text>
                                </>}
                            </span>
                        </div>
                    </div>
                    {isInTheFuture || isRegistrationOpen ? <>
                        <UserProfileInline stores={this.props.stores} showDone={true} />
                    </> : <></>}
                </div>
            </> : <></>} */}

        </>);
    }
}
export default ConventionViewSignup;
